"use client";

import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { BagSdkConfig } from "@repo/bag";
import { RouterProvider } from "@repo/next-utils/router";
import type { EnrichedCountry } from "@repo/store-presentation";
import * as Auth from "@repo/ui/auth";
import * as Bag from "@repo/ui/bag";
import * as SavedItems from "@repo/ui/saved-items";
import * as Store from "@repo/ui/store";
import { AnalyticsProvider } from "@/components/analytics/initialise-analytics";
import { ClientContextManager } from "@/components/client-context-manager";
import { FloorProvider } from "@/components/floor-context/floor-context";
import { APP_ENVIRONMENT } from "@/constants";
import { HeartIcon } from "@/theme/icons/heart";
import { clientRequest } from "../utils/request-adapters/client-request";

type ClientProps = {
  children: React.ReactNode;
  countryData: EnrichedCountry;
  keyStoreDataversion: string;
  shop: string;
  versionManifest: VersionManifest;
};

const BAG_EXPANSION_SCOPES = {
  default: ["summary"] as BagSdkConfig["initialBagExpansions"],
  all: [
    "summary",
    "total",
    "getDeliveryOptions",
  ] as BagSdkConfig["initialBagExpansions"],
} as const satisfies Record<string, BagSdkConfig["initialBagExpansions"]>;

const authScopes = ["openid", "profile", "sensitive"];

const clientId = APP_ENVIRONMENT === "prod" ? "topshop" : "chameleon-www";

const appRouterPathPrefixes = [
  "/topshop/category",
  "/topman/category",
  "/product",
  "/bag",
  "/search",
  "/delivery-and-returns",
  "/wishlist",
];

function BagProviderWithRouteScoping({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const isBagRoute = pathname?.endsWith("/bag");

  const bagExpansions = useMemo(
    () =>
      isBagRoute ? BAG_EXPANSION_SCOPES.all : BAG_EXPANSION_SCOPES.default,
    [isBagRoute],
  );

  return (
    <Bag.Provider
      bagExpansions={bagExpansions}
      chameleonRequestInstance={clientRequest}
    >
      {children}
    </Bag.Provider>
  );
}

export function Client({
  children,
  countryData,
  keyStoreDataversion,
  shop,
  versionManifest,
}: ClientProps) {
  return (
    <RouterProvider
      appRouterPathPrefixes={appRouterPathPrefixes}
      siteBasePath={countryData.siteBasePath}
    >
      <Store.Provider
        countryData={countryData}
        keyStoreDataversion={keyStoreDataversion}
        shop={shop}
        versionManifest={versionManifest}
      >
        <FloorProvider>
          <Auth.Provider
            clientId={clientId}
            scopes={authScopes}
            clientType="WWW"
          >
            <BagProviderWithRouteScoping>
              <SavedItems.Provider
                chameleonRequestInstance={clientRequest}
                SavedIcon={<HeartIcon filled label={"Remove from wishlist"} />}
                UnsavedIcon={<HeartIcon label={"Add to wishlist"} />}
              >
                <AnalyticsProvider>{children}</AnalyticsProvider>
              </SavedItems.Provider>
            </BagProviderWithRouteScoping>
          </Auth.Provider>
          <ClientContextManager />
        </FloorProvider>
      </Store.Provider>
    </RouterProvider>
  );
}

Client.displayName = "Client";
